<template>
<div class="container-fluid">
  <div class="row">
    <div class="col-12">
            
    	<div class="d-flex align-items-center">
	    	<h1 class="flex-grow-1" data-test="pageTitle">All Users</h1>
        <router-link
        	v-if="perms.can_create_user"
        	:to="{ name: 'users.create' }"
        	v-tooltip.bottom="'Add User'"
        	class="btn btn-sm btn-ghost-primary text-gray-500 leading-none"
          data-test="addUserButton">
          <i class="far fa-plus-circle text-xl"></i>
        </router-link>
	    </div>

			<filter-bar
				class="d-flex justify-content-between"
				filter_type="client"
			  @filter-set="setFilter($event)"
			  @filter-client-select="selectClientFilter($event)"
			  @filter-reset="resetAllFilters()"
        data-test="filterBar"
			/>


	    <div class="card leading-tight">
	      <vuetable ref="vuetable"
	        api-url="/api/admin/users_paged"
	        :fields="fields"
	        data-path="data"
	        pagination-path=""
	        :css="css.table"
	        :sort-order="sortOrder"
	        :sort-params="getSortParam"
	        :multi-sort="false"
	        :append-params="moreParams"
	        :no-data-template="emptyText"
	        @vuetable:load-success="onDataLoaded"
	        @vuetable:pagination-data="onPaginationData">

	        <div slot="last_name" slot-scope="props" class="d-flex align-items-center">
	          <div class="rounded-circle circle-cropped avatar-sm mr-2 font-weight-bold text-sm text-center">
	            <img :src="props.rowData.photo_url" class="img-fluid">
	          </div>

	          <div>
	            <router-link :to="{ name: 'users.show', params:{ id:props.rowData.id } }">{{ props.rowData.display_name }}</router-link>
	            <div>
	              <span v-for="role in props.rowData.roles" class="badge bg-blue-100 text-blue-700 mr-1">{{ role.name }}</span>
	            </div>
	          </div>
	        </div>

	        <template slot="client_list_all" slot-scope="props">
						<div v-if="props.rowData.roles.some(role => role.name === 'sitewide admin')" class="text-gray-700">
							<em>All clients (admin)</em>
						</div>

						<template v-else>
							<div v-if="props.rowData.org" class="mb-1 font-weight-bold">
								<router-link :to="{ name: 'orgs.show', params: { id: props.rowData.org.id } }">
									<i class="fal fa-sitemap fa-fw"></i> {{ props.rowData.org.name }}
								</router-link>
							</div>
							<router-link v-for="client in props.rowData.client_list_all" :key="client.id" :to="{ name: 'clients.show', params: { id: client.id } }">
								{{ client.name}}<br>
							</router-link>
						</template>
	        </template>

	        <template slot="last_seen_overall" slot-scope="props" v-if="perms.can_edit_any_user">
						<template v-if="perms.can_create_client">
							<span class="text-gray-500">Sitewide:</span> {{ props.rowData.last_seen_overall ? moment(props.rowData.last_seen_overall).format('MMM D, Y') : 'Never' }} <br>
							<span class="text-gray-500">Client:</span> {{ props.rowData.last_seen_here ? moment(props.rowData.last_seen_here).format('MMM D, Y') : 'Never' }} 
						</template>
						<template v-else>
								{{ props.rowData.last_seen_here ? moment(props.rowData.last_seen_here).format('MMM D, Y') : 'Never' }}
						</template>	
	        </template>

	        <template slot="actions" slot-scope="props">
	          <div class="drop-down dropleft">
	            <a href="#" role="button" id="dropdownMenuLink" data-toggle="dropdown" data-test="contextMenu">
	            	<i class="far fa-ellipsis-h text-xl"></i>
	            </a>

							<div class="dropdown-menu py-0" aria-labelledby="dropdownMenuLink">
	              <router-link :to="{ name: 'users.show', params: { id: props.rowData.id } }" class="dropdown-item" data-test="viewUserLink">
	              	Profile
	              </router-link>
	              <router-link v-if="props.rowData.id === user.id" :to="{ name: 'users.edit', params: { id: props.rowData.id } }" class="dropdown-item" data-test="editUserLink">
	              	Edit User
	              </router-link>
	              <router-link v-if="props.rowData.can.update && props.rowData.id != user.id" :to="{ name: 'users.settings', params: { id: props.rowData.id } }" class="dropdown-item" data-test="editUserLink">
	              	Edit User
	              </router-link>                                
								<a v-if="user.can.impersonate && props.rowData.can.beImpersonated" class="dropdown-item" :href="'/impersonate/take/' + props.rowData.id">
									Impersonate
								</a>
	              <template v-if="props.rowData.invite && perms.can_create_user">
	              	<span class="dropdown-item" @click="resendInvite(props.rowData.id)" data-test="reinviteUserLink">Resend Invite</span>
	              	<span class="dropdown-item" @click="deleteInvite(props.rowData.id)" data-test="deleteUserLink">Delete User</span>
	              </template>                           
	            </div>

	          </div>
	        </template>        
	      </vuetable>
	    </div>

	    <div class="background-fix vuetable-pagination d-flex mb-3">
	      <vuetable-pagination-info
	        ref="paginationInfo"
	        class="flex-fill"
	        info-class="pagination-info"
	        info-template="{from} to {to} of {total} items" />

	      <vuetable-pagination-bootstrap
	        ref="pagination"
	        :css="css.pagination"
	        class="flex-fill text-right"
	        @vuetable-pagination:change-page="onChangePage" />
	    </div>

	  </div>
	</div>
</div>
</template>

<script>

  import { mapState } from 'vuex';
  import BreadcrumbsManager from '../../helpers/BreadcrumbManager';
  import Vuetable from 'vuetable-2';
  import VuetablePaginationInfo from 'vuetable-2/src/components/VuetablePaginationInfo';
  import FilterBar from '../../components/FilterBar';
  import VuetablePaginationBootstrap from '../../components/VuetablePaginationBootstrap.vue';

  export default {
      
    mixins: [BreadcrumbsManager],

    components: {
      Vuetable,
      VuetablePaginationInfo,
      VuetablePaginationBootstrap,
      FilterBar,
    },

    props: {
      //sort_field: String,
      //sort_dir: String,
    },

    data() {
      return {
        emptyText: 'Loading...please wait',

        fields: [           
          {
            name: 'last_name',
            title: 'Name',
            sortField: 'last_name',
          },
          { 
            name: 'client_list_all',
            title: 'Clients',

          },
          {
            name: 'active_text',
            title: 'Status',
          },
          {
          	name: 'last_seen_overall',
          	title: 'Last Seen',
          	sortField: 'last_seen_overall',
          	titleClass: 'hidden_mobile',
            dataClass: 'hidden_mobile',
          },         
          {
            name: 'actions',
            title: '',
          },
        ],

        css: {
          table: {
            tableClass: 'table users-table',
            ascendingIcon: 'fas fa-sort-alpha-down',
            descendingIcon: 'fas fa-sort-alpha-up'
          },

          pagination: {
            wrapperClass: 'pagination',
            activeClass: 'active',
            disabledClass: 'disabled',
            pageClass: 'page',
            linkClass: 'link',
            icons: {
              first: '',
              prev: '',
              next: '',
              last: '',
            },
          },

          icons: {
            first: 'fas fa-angle-double-left',
            prev: 'fas fa-angle-left',
            next: 'fas fa-angle-right',
            last: 'fas fa-angle-double-right',
          },
        },

        sortOrder: [
          {
            field: 'last_name',
            direction: 'asc',
          }
        ],

        moreParams: {
        },
      }
    },

    created() {
    },

    activated() {
      this.setBreadcrumbs([
          { text: 'Home', to: { name: 'home' } },
          { text: 'Users', to: { name: 'users.index' } },
      ]);
      Vue.nextTick( () => this.$refs.vuetable.refresh() )
    },

    methods: {
			deleteInvite(id) {
				Swal.fire({
					title: 'Are you sure?',
					text: 'You will not be able to recover this user.',
					icon: 'warning',
					showCancelButton: true,
					cancelButtonText: 'Keep user',
					confirmButtonText: 'Delete user',
					animation: false,
					}).then((result) => {
						if (result.value) {         	
							axios.delete('/api/admin/users/force_delete/' + id).then(() => {
								Swal.fire({
									toast: true,
									position: 'top-end',
									showConfirmButton: false,
									timer: 4000,
									icon: 'success',
									title: 'User deleted',
								});
								this.users.splice(this.userIndex(id), 1);
							});
						}
					});
			},

      getSortParam: function(sortOrder) {
        return sortOrder.map(function(sort) {
          return (sort.direction === 'desc' ? '-' : '') + sort.field
        }).join(',')
      },

      onPaginationData (paginationData) {
        this.$refs.pagination.setPaginationData(paginationData)
        this.$refs.paginationInfo.setPaginationData(paginationData)
      },

      onChangePage (page) {
        this.$refs.vuetable.changePage(page)
      },

      selectClientFilter(filterClientId) {
        this.moreParams = {
          'filter[search_client_list]': filterClientId
        }
        Vue.nextTick( () => this.$refs.vuetable.refresh() )
      },

      setFilter(filterTextName) {
        this.moreParams = {
          'filter[search_full_name]': filterTextName
        }
        Vue.nextTick( () => this.$refs.vuetable.refresh() )
      },
      
      resetAllFilters() {
        this.moreParams = {}
        Vue.nextTick( () => this.$refs.vuetable.refresh() )
      },

      onDataLoaded() {
        Vue.nextTick( () => {
          if (this.$refs.vuetable.tableData.length == 0) {
            this.emptyText = 'No records found';
          }
        });          
      },

			resendInvite(id) {
				axios.post('/api/admin/users/resend_invite/' + id).then(() => {
					Swal.fire({
						toast: true,
						position: 'top-end',
						showConfirmButton: false,
						timer: 4000,
						icon: 'success',
						title: 'Invitation sent',
					});
				});
			},

			userIndex(id) {
				return this.users.findIndex((user) => {
					return user.id === id;
				});
			},

    },

    computed: mapState(['perms', 'user']),

  }
</script>

<style scoped>
	tr:hover .drop-down { 
    opacity:1;
    transition:.2s all ease-out;
  }
</style>